import { AI_UPLOAD_KINDS } from '@repo/lib';
import { z } from '../zod';

// The dashboard mints one per conversation with crypto.randomUUID().
export const AiChatConversationIdValidationSchema = z.uuid();

export const AiChatModeValidationSchema = z.enum(['edit', 'ask']);

// Booleans rather than literal `true`: the iOS client's swift-openapi-generator has no literal types.
export const AiChangeFieldValidationSchema = z.object({
  key: z.string(),
  before: z.unknown().optional(),
  after: z.unknown().optional(),
  truncated: z.boolean().optional(),
});

export const AiChangeRecordValidationSchema = z.object({
  id: z.string(),
  tool: z.string(),
  op: z.enum(['create', 'update', 'delete']),
  target: z.object({
    kind: z.enum(['page', 'product', 'field', 'media']),
    id: z.string().optional(),
  }),
  fields: z.array(AiChangeFieldValidationSchema),
  captureFailed: z.boolean().optional(),
});

export const AiPointValidationSchema = z.object({
  n: z.number(),
  target: z.string(),
  fieldId: z.string().optional(),
  title: z.string(),
  detail: z.string(),
  fix: z.string().optional(),
  upload: z.enum(AI_UPLOAD_KINDS).optional().meta({
    description:
      "The spot needs the seller's own images or files; the card offers an uploader instead of a fix.",
  }),
});

export const AiUploadOfferValidationSchema = z.object({
  kind: z.enum(AI_UPLOAD_KINDS),
  note: z.string().optional(),
});

export const AiChatToolValidationSchema = z.object({
  id: z.string(),
  name: z.string(),
  step: z
    .string()
    .optional()
    .meta({ description: "The model's words for the step. Absent on older turns." }),
  ok: z.boolean().optional().meta({ description: 'Absent when the call never finished.' }),
});

export const AiChatAttachmentValidationSchema = z.object({
  fileId: z.string(),
  name: z.string(),
  type: z
    .string()
    .meta({ description: 'The MIME type, like image/png, application/pdf or text/csv.' }),
  url: z
    .string()
    .optional()
    .meta({ description: 'The public image URL. Absent on a document, which is private.' }),
});

export const AI_CHAT_TURN_STATUSES = ['done', 'error', 'stopped'] as const;

export const AiChatStoppedReasonValidationSchema = z.enum(['limit', 'timeout']);

export const AiChatTurnValidationSchema = z.object({
  id: z.string(),
  conversationId: z.string(),
  message: z.string(),
  attachments: z.array(AiChatAttachmentValidationSchema).optional().meta({
    description:
      'Images, PDFs, CSV and text files the seller attached to the message. Absent when there were none.',
  }),
  mode: AiChatModeValidationSchema,
  guide: z.string().optional(),
  reply: z.string().meta({ description: 'The reply as streamed, em dashes already replaced.' }),
  tools: z.array(AiChatToolValidationSchema),
  changes: z.array(AiChangeRecordValidationSchema),
  points: z.array(AiPointValidationSchema),
  uploads: z.array(AiUploadOfferValidationSchema).optional().meta({
    description: 'Uploaders the AI offered the seller. Absent from an API older than uploads.',
  }),
  status: z.enum(AI_CHAT_TURN_STATUSES).meta({
    description: 'stopped: the client went away mid-run. error: the seller was shown `error`.',
  }),
  error: z.string().optional(),
  stoppedReason: AiChatStoppedReasonValidationSchema.optional(),
  responseId: z
    .string()
    .optional()
    .meta({ description: 'Send as previousResponseId to continue the conversation.' }),
  createdAt: z.string(),
});

export const AiChatHistoryResponseValidationSchema = z.object({
  conversationId: z.string().nullable(),
  turns: z.array(AiChatTurnValidationSchema).meta({ description: 'Oldest first.' }),
});

export type AiChangeField = z.infer<typeof AiChangeFieldValidationSchema>;
export type AiChangeRecord = z.infer<typeof AiChangeRecordValidationSchema>;
export type AiPoint = z.infer<typeof AiPointValidationSchema>;
export type AiUploadOffer = z.infer<typeof AiUploadOfferValidationSchema>;
export type AiChatAttachment = z.infer<typeof AiChatAttachmentValidationSchema>;
export type AiChatTool = z.infer<typeof AiChatToolValidationSchema>;
export type AiChatTurnStatus = (typeof AI_CHAT_TURN_STATUSES)[number];
export type AiChatStoppedReason = z.infer<typeof AiChatStoppedReasonValidationSchema>;
export type AiChatTurn = z.infer<typeof AiChatTurnValidationSchema>;
export type AiChatHistoryResponse = z.infer<typeof AiChatHistoryResponseValidationSchema>;
